import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import useToast from "../../../../util/useToast";
import {useCallback, useEffect, useState} from "react";
import {MODE_EDIT} from "../../../../codes";
import {
  editorConfigSelector,
  rxHideTagEditor,
  rxShowHideDeleteConfirmationModal,
  rxUpdateTags,
  selectAllTags
} from "../../../../slices/tagsSlice";
import {createTag, deleteTag, updateTag} from "../../../../api/ApiTags";
import {getOrDefault} from "../../../../util/util";
import {DEFAULT_COLOR} from "../../../../common/ColorBox";
import useNetwork from "../../../../util/useNetwork";
import useValidator from "../../../../util/useValidator";

const useTagEditor = () => {
  const dispatch = useDispatch();

  const {t} = useTranslation("settings_tags");
  const {showToastSuccess} = useToast();
  const {handleNetworkError} = useNetwork();
  const validate = useValidator();

  const tags = useSelector(selectAllTags);
  const {show, mode, tag, deleteConfirmationModal} = useSelector(editorConfigSelector);

  const [editedTag, setEditedTag] = useState({errors: {}, name: "", description: ""});

  const hideTagEditor = () => dispatch(rxHideTagEditor());

  useEffect(() => {
    if (!tag || !show) {
      return;
    }

    setEditedTag({
      errors: {},
      name: getOrDefault(tag.name, ""),
      description: getOrDefault(tag.description, ""),
      color: getOrDefault(tag.color, DEFAULT_COLOR)
    });
  }, [show, tag]);

  const getErrors = values => {
    const errors = {};

    if (!values.name.trim().length) {
      errors.name = "tag_editor_error_name_must_be_filled";
    } else {
      const existingTag = tags.find(t => t.name === values.name);

      if (existingTag && existingTag !== tag) {
        errors.name = "tag_editor_error_name_already_exists";
      }
    }

    return errors;
  };

  const submit = e => {
    e.preventDefault();

    const values = {
      name: editedTag.name, description: editedTag.description, color: editedTag.color
    };

    return validate(getErrors(values))
      .then(() => {
        if (mode === MODE_EDIT) {
          return updateTag(tag.id, values);
        } else {
          return createTag(values);
        }
      })
      .then(result => dispatch(rxUpdateTags(result)))
      .then(() => {
        showToastSuccess(t(mode === MODE_EDIT ? "tag_edited" : "tag_added"));

        hideTagEditor();
      })
      .catch(error => {
        handleNetworkError(error, {
          400: err => setEditedTag(prev => ({...prev, errors: err.response.data}))
        });
      });
  };

  const updateEditedTag = field => event => {
    const values = {};

    switch (field) {
      case "color":
        values.color = event;
        break;
      default:
        values[field] = event.target.value;
        break;
    }

    setEditedTag(prev => ({...prev, ...values}));
  };

  const showDeleteConfirmationModal = useCallback(() => dispatch(rxShowHideDeleteConfirmationModal(true)), [dispatch]);
  const hideDeleteConfirmationModal = useCallback(() => dispatch(rxShowHideDeleteConfirmationModal(false)), [dispatch]);

  const onDeleteTag = () => {
    return deleteTag(tag.id)
      .then(result => {
        dispatch(rxUpdateTags(result));
        dispatch(rxHideTagEditor());
      })
      .catch(handleNetworkError)
      .finally(hideDeleteConfirmationModal);
  };

  return {
    show,
    t,
    mode,
    hideTagEditor,
    editedTag,
    updateEditedTag,
    submit,
    deleteConfirmationModal,
    showDeleteConfirmationModal,
    hideDeleteConfirmationModal,
    onDeleteTag
  };
};

export default useTagEditor;
